// @ts-nocheck
/**
 * ターン管理モジュール
 * 速度順での行動順決定と管理
 * @module twinKey/core/battle/TurnManager
 */

// ============================================================
// TurnManager クラス
// ============================================================

export class TurnManager {
  /**
   * @param {Object} state - バトル状態への参照
   */
  constructor(state) {
    this.state = state;
  }

  /**
   * 行動順を計算
   * 速度が高い順、同速はランダム
   * @returns {string[]} ユニットIDの配列
   */
  calculateTurnOrder() {
    const allUnits = [
      ...this.state.allies.filter(u => u.isAlive),
      ...this.state.enemies.filter(u => u.isAlive),
    ];

    // 速度でソート（降順）、同速はランダム
    const sorted = allUnits
      .map(u => ({ ...u, sortKey: u.stats.speed + Math.random() * 0.001 }))
      .sort((a, b) => b.sortKey - a.sortKey)
      .map(u => u.id);

    return sorted;
  }

  /**
   * 現在行動中のユニットを取得
   * @returns {Object | null} 現在のユニット
   */
  getCurrentUnit() {
    const unitId = this.state.turnOrder[this.state.currentTurnIndex];
    if (!unitId) return null;

    const allUnits = [...this.state.allies, ...this.state.enemies];
    return allUnits.find(u => u.id === unitId) || null;
  }

  /**
   * 次のユニットに進む
   * @returns {{ newIndex: number, turnEnded: boolean }}
   */
  advanceToNextUnit() {
    let newIndex = this.state.currentTurnIndex + 1;
    let turnEnded = false;

    // 全員行動完了した場合
    if (newIndex >= this.state.turnOrder.length) {
      turnEnded = true;
      newIndex = 0;
    }

    // 死亡ユニットはスキップ
    while (newIndex < this.state.turnOrder.length) {
      const unitId = this.state.turnOrder[newIndex];
      const unit = this.findUnit(unitId);

      if (unit && unit.isAlive) {
        break;
      }

      newIndex++;
      if (newIndex >= this.state.turnOrder.length) {
        turnEnded = true;
        newIndex = 0;
        break;
      }
    }

    return { newIndex, turnEnded };
  }

  /**
   * ユニットを検索
   * @param {string} unitId - ユニットID
   * @returns {Object | null}
   */
  findUnit(unitId) {
    const allUnits = [...this.state.allies, ...this.state.enemies];
    return allUnits.find(u => u.id === unitId) || null;
  }

  /**
   * 行動順の表示用配列を取得
   * @returns {Array} 行動順情報配列
   */
  getTurnOrderDisplay() {
    return this.state.turnOrder.map((unitId, index) => {
      const unit = this.findUnit(unitId);
      if (!unit) return null;

      return {
        id: unitId,
        name: unit.name,
        isAlly: unit.isAlly,
        isAlive: unit.isAlive,
        isCurrent: index === this.state.currentTurnIndex,
        speed: unit.stats.speed,
        hpPercent: Math.floor((unit.stats.currentHp / unit.stats.maxHp) * 100),
      };
    }).filter(Boolean);
  }

  /**
   * 残り行動ユニット数を取得
   * @returns {{ allies: number, enemies: number }}
   */
  getRemainingActors() {
    const remaining = this.state.turnOrder
      .slice(this.state.currentTurnIndex)
      .map(id => this.findUnit(id))
      .filter(u => u && u.isAlive);

    return {
      allies: remaining.filter(u => u.isAlly).length,
      enemies: remaining.filter(u => !u.isAlly).length,
    };
  }

  /**
   * ターン終了時の処理
   * - クールダウン減少
   * - 防御状態リセット
   */
  processTurnEnd() {
    const allUnits = [...this.state.allies, ...this.state.enemies];

    for (const unit of allUnits) {
      if (!unit.isAlive) continue;

      // クールダウン減少
      for (const skill of unit.skills) {
        if (skill.currentCd > 0) {
          skill.currentCd--;
        }
      }

      // 防御状態リセット
      unit.isDefending = false;
    }
  }

  /**
   * 新しいターンを開始
   */
  startNewTurn() {
    this.state.turnCount++;
    this.state.currentTurnIndex = 0;
    this.state.turnOrder = this.calculateTurnOrder();
  }
}

// ============================================================
// ヘルパー関数
// ============================================================

/**
 * 速度から行動順位を取得（表示用）
 * @param {number} speed - 速度値
 * @returns {string} 速度ランク
 */
export function getSpeedRank(speed) {
  if (speed >= 100) return 'S';
  if (speed >= 80) return 'A';
  if (speed >= 60) return 'B';
  if (speed >= 40) return 'C';
  if (speed >= 20) return 'D';
  return 'E';
}

/**
 * 速度比較
 * @param {Object} unit1 - ユニット1
 * @param {Object} unit2 - ユニット2
 * @returns {number} 比較結果（負: unit1が速い、正: unit2が速い）
 */
export function compareSpeed(unit1, unit2) {
  const diff = unit2.stats.speed - unit1.stats.speed;
  if (diff !== 0) return diff;

  // 同速の場合はランダム
  return Math.random() > 0.5 ? 1 : -1;
}
